/* Shared by the app and its service worker. No login refresh tokens are stored here. */
;(function (root) {
  const DB = 'atttrack-temporary-videos'
  const STORE = 'uploads'
  const TAG = 'atttrack-video-uploads'
  const LEASE_MS = 5 * 60 * 1000
  function open() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB, 1)
      request.onupgradeneeded = () => request.result.createObjectStore(STORE, { keyPath: 'id' })
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
  }
  async function access(mode, work) {
    const db = await open()
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, mode)
      let result
      tx.oncomplete = () => { db.close(); resolve(result) }
      tx.onerror = tx.onabort = () => { db.close(); reject(tx.error || new Error('Temporary video storage failed')) }
      work(tx.objectStore(STORE), value => { result = value })
    })
  }
  const list = () => access('readonly', (store, done) => { store.getAll().onsuccess = event => done(event.target.result) })
  const put = job => access('readwrite', (store, done) => { store.put(job); done(job) })
  const remove = id => access('readwrite', store => store.delete(id))
  const change = (id, update) => access('readwrite', (store, done) => {
    store.get(id).onsuccess = event => {
      const current = event.target.result
      if (!current) return done(null)
      const next = update(current)
      if (next) store.put(next)
      done(next)
    }
  })
  async function timedFetch(url, options) {
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), 60000)
    try { return await fetch(url, { ...options, signal: controller.signal }) }
    finally { clearTimeout(timer) }
  }
  async function request(job, path, body) {
    const response = await timedFetch(job.baseUrl + path, {
      method: 'POST', headers: { Authorization: job.authorization, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    const result = await response.json()
    if (!response.ok) throw Object.assign(new Error(result.error || 'Video upload will retry'), { status: response.status })
    return result
  }
  async function process(job) {
    // The server acknowledgement may have been lost when the app closed.
    await request(job, '/v1/attendance-actions/punch', job.punch)
    const path = `/v1/attendance-actions/${encodeURIComponent(job.id)}/video`
    const ticket = await request(job, path + '/ticket', { contentType: job.blob.type || 'video/webm', size: job.blob.size })
    if (!ticket.complete) {
      if (!ticket.uploaded) {
        const response = await timedFetch(ticket.uploadUrl, {
          method: 'PUT', headers: { 'Content-Type': job.blob.type || 'video/webm' }, body: job.blob,
        })
        if (!response.ok) throw new Error('Video upload interrupted; it will retry automatically')
      }
      // Only server-verified R2 bytes AND the persisted attendance link permit deletion.
      const confirmation = await request(job, path + '/complete', {})
      if (confirmation.complete !== true) throw new Error('Server has not confirmed the video attachment')
    }
  }
  async function drain(uid) {
    let failed = false
    for (const candidate of await list()) {
      if (uid && candidate.uid !== uid) continue
      if (!candidate.authorization) { failed = true; continue }
      const lease = crypto.randomUUID()
      const job = await change(candidate.id, current => {
        if (current.leaseUntil > Date.now()) return null
        return { ...current, lease, leaseUntil: Date.now() + LEASE_MS }
      })
      if (!job) { failed = true; continue }
      try {
        await process(job)
        await remove(job.id)
      } catch (error) {
        failed = true
        await change(job.id, current => current.lease === lease
          ? { ...current, leaseUntil: 0, error: error.status === 401 ? 'Reopen and sign in to finish the video upload.' : 'Upload pending. It will retry when connected.' }
          : null)
      }
    }
    return { failed }
  }
  root.AttendanceVideoQueue = { list, put, remove, change, drain, TAG }
  if (typeof ServiceWorkerGlobalScope !== 'undefined' && root instanceof ServiceWorkerGlobalScope) {
    const notify = async () => {
      for (const client of await root.clients.matchAll({ type: 'window' })) client.postMessage({ type: 'attendance-videos-updated' })
    }
    const run = async () => { const result = await drain(); await notify(); if (result.failed) throw new Error('Attendance videos remain pending') }
    root.addEventListener('sync', event => { if (event.tag === TAG) event.waitUntil(run()) })
    root.addEventListener('message', event => {
      if (event.data?.type !== 'attendance-videos-upload') return
      event.waitUntil(run().catch(async () => {
        if (root.registration.sync) await root.registration.sync.register(TAG)
      }))
    })
  }
})(globalThis)
